import { useCallback, useEffect, useMemo, useState } from 'react';
import { getStocks } from '../services/stocksService';
import { formatStockDateTime } from '../utils/stocksUtils';

/**
 * /sales /tickets /sale-report sayfalarında eventID ile oturum kapsamı.
 * scopeStockId null = Tümü.
 * skipNetwork + preloadedStocks: sale-report zaten stocks getirir, ekstra istek yok.
 */
export const useEventSessionScopeByEventId = (
  eventId,
  initialStockId = null,
  { preloadedStocks = null, preloadedDateType = null, skipNetwork = false } = {},
) => {
  const usePreload = skipNetwork || Array.isArray(preloadedStocks);
  const [stocks, setStocks] = useState(() =>
    Array.isArray(preloadedStocks) ? preloadedStocks : [],
  );
  const [dateType, setDateType] = useState(preloadedDateType ?? null);
  const [loading, setLoading] = useState(Boolean(eventId) && !usePreload);
  const [scopeStockId, setScopeStockId] = useState(initialStockId || null);

  useEffect(() => {
    setScopeStockId(initialStockId || null);
  }, [initialStockId, eventId]);

  useEffect(() => {
    if (!usePreload) return;
    const list = Array.isArray(preloadedStocks) ? [...preloadedStocks] : [];
    list.sort((a, b) => {
      const da = `${a.date || ''} ${a.time || ''}`;
      const db = `${b.date || ''} ${b.time || ''}`;
      return da.localeCompare(db);
    });
    setStocks(list);
    setDateType(preloadedDateType ?? (list.length >= 2 ? 'multi' : null));
    setLoading(false);
    setScopeStockId((prev) => {
      if (prev && list.some((s) => s.id === prev || s.stockID === prev)) return prev;
      if (
        initialStockId &&
        list.some((s) => s.id === initialStockId || s.stockID === initialStockId)
      ) {
        return initialStockId;
      }
      return null;
    });
  }, [usePreload, preloadedStocks, preloadedDateType, initialStockId]);

  const reload = useCallback(async () => {
    if (usePreload) return;
    if (!eventId) {
      setStocks([]);
      setDateType(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // dateType için ayrı events/:id GET yapma — 2+ stock varsa select göster
      const rows = await getStocks({
        eventID: eventId,
        limitCount: 200,
        orderByField: 'date',
        orderDirection: 'asc',
      });
      const list = Array.isArray(rows) ? rows : [];
      list.sort((a, b) => {
        const da = `${a.date || ''} ${a.time || ''}`;
        const db = `${b.date || ''} ${b.time || ''}`;
        return da.localeCompare(db);
      });
      setDateType(list.length >= 2 ? 'multi' : 'oneoff');
      setStocks(list);
      setScopeStockId((prev) => {
        if (prev && list.some((s) => s.id === prev || s.stockID === prev)) return prev;
        if (
          initialStockId &&
          list.some((s) => s.id === initialStockId || s.stockID === initialStockId)
        ) {
          return initialStockId;
        }
        return null;
      });
    } catch (error) {
      console.error('useEventSessionScopeByEventId:', error);
      setStocks([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, initialStockId, usePreload]);

  useEffect(() => {
    if (!usePreload) reload();
  }, [reload, usePreload]);

  const showSelect = stocks.length >= 2 && (dateType == null || dateType !== 'oneoff');

  const sessionOptions = useMemo(
    () =>
      stocks.map((s) => ({
        id: s.id,
        label: formatStockDateTime(s.date, s.time) || s.id,
      })),
    [stocks],
  );

  return {
    stocks,
    loading,
    showSelect,
    sessionOptions,
    scopeStockId,
    setScopeStockId,
    dateType,
  };
};
